import { useEffect, useState } from 'react';
import { delivererClient } from '../services/deliverer/DelivererClient';
import { Delivery } from '../models/OrderHistory/OrderHistory';
import { SalesOrderItemResponse } from '../services/deliverer/types';
import { useQuery } from 'react-query';
import { useActiveWeeklyMenus } from './useActiveWeeklyMenu';
import { getWeeklyMenuKeyForDate } from '../helpers/weeklyMenu';

interface State {
  readonly deliveries: Delivery[];
  readonly error: boolean;
  readonly loading: boolean;
  readonly metadata?: {
    pageCount?: number;
    totalItemCount?: number;
    pageNumber?: number;
    pageSize?: number;
    hasPreviousPage?: boolean;
    hasNextPage?: boolean;
    isFirstPage?: boolean;
    isLastPage?: boolean;
    firstItemOnPage?: number;
    lastItemOnPage?: number;
  };
}

const initialState: State = {
  deliveries: [],
  error: false,
  loading: true,
  metadata: undefined
};

// Helper function to map sales order items with product info
async function mapSalesOrderItemsWithProductInfo(salesOrderItems: SalesOrderItemResponse[]) {
  return Promise.all(
    salesOrderItems.map(async (salesOrderItem) => {
      try {
        const product = await delivererClient.getProductInformation(salesOrderItem.productId);

        return {
          salesOrderItemId: salesOrderItem.salesOrderItemId,
          productId: salesOrderItem.productId,
          productName: product.name ?? '', // fallback if not available
          portion: product.portion ?? '', // fallback if not available
          quantity: salesOrderItem.quantity,
          parentSalesOrderItemId: salesOrderItem.parentSalesOrderItemId ?? null,
          rejectionStatus: salesOrderItem.rejectionStatus,
          category: product.category ?? '', // fallback if not available
          sequenceNumberOnWeeklyMenu: '-' // This will be populated later when we have the weekly menu data
        };
      } catch (err) {
        return {
          salesOrderItemId: salesOrderItem.salesOrderItemId,
          productId: salesOrderItem.productId,
          productName: salesOrderItem.productId,
          portion: '',
          quantity: salesOrderItem.quantity,
          parentSalesOrderItemId: salesOrderItem.parentSalesOrderItemId ?? null,
          rejectionStatus: salesOrderItem.rejectionStatus,
          category: undefined, // fallback if not available
          sequenceNumberOnWeeklyMenu: '-' // This will be populated later when we have the weekly menu data
        };
      }
    })
  );
}

export const useSingleSalesOrderHistory = (salesOrderId: string | undefined) => {
  const { activeWeeklyMenus } = useActiveWeeklyMenus();

  const { data, error, isLoading, isRefetching, refetch } = useQuery(
    ['singleSalesOrderHistory', salesOrderId, activeWeeklyMenus],
    async () => {
      if (!salesOrderId) {
        return;
      }

      const order = await delivererClient.getOrderBySalesOrderId(salesOrderId);

      const weeklyMenuKeysToFetch = Array.from(
        new Set(
          order.deliveries
            .map((delivery) =>
              getWeeklyMenuKeyForDate(delivery.requestedDeliveryMoment, activeWeeklyMenus)
            )
            .filter((key): key is string => !!key)
        )
      );

      const deliveries = await Promise.all(
        order.deliveries.map(async (delivery) => {
          return {
            salesOrderId: order.salesOrderId,
            deliveryId: delivery.deliveryId,
            status: delivery.status,
            processStatus: delivery.processStatus,
            rejectionStatus: delivery.rejectionStatus,
            deliveryStatus: delivery.deliveryStatus,
            requestedDeliveryMoment: delivery.requestedDeliveryMoment,
            alternateDeliveryAddress: delivery.alternateDeliveryAddress,
            items: await mapSalesOrderItemsWithProductInfo(delivery.salesOrderItems),
            actions: delivery.actions
          };
        })
      );

      const weeklyMenusByKey = new Map(
        await Promise.all(
          weeklyMenuKeysToFetch.map(
            async (key) => [key, await delivererClient.getWeeklyMenu(key)] as const
          )
        )
      );

      // Append sequence numbers from the weekly menu to the delivery items
      deliveries.forEach((delivery) => {
        const weeklyMenuKey = getWeeklyMenuKeyForDate(
          delivery.requestedDeliveryMoment,
          activeWeeklyMenus
        );
        const matchingWeeklyMenu = weeklyMenuKey ? weeklyMenusByKey.get(weeklyMenuKey) : undefined;

        if (matchingWeeklyMenu) {
          delivery.items.forEach((item) => {
            const matchingMenuItem = matchingWeeklyMenu.items.find((menuItem) =>
              menuItem.portions?.some((portion) => portion.productId === item.productId)
            );
            if (matchingMenuItem) {
              item.sequenceNumberOnWeeklyMenu = matchingMenuItem.mealSequenceNumber;
            }
          });
        }
      });

      return deliveries;
    },
    {
      enabled: !!salesOrderId,
      staleTime: 1000 * 60 * 1, // 1 minute
      refetchOnWindowFocus: false
    }
  );

  return {
    deliveries: data ?? [],
    error: !!error,
    loading: isLoading || isRefetching,
    refetch
  };
};

export const useSalesOrderHistory = (customerNumber?: string, defaultPageSize: number = 10) => {
  const [state, setState] = useState<State>(initialState);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  const { activeWeeklyMenus } = useActiveWeeklyMenus();

  useEffect(() => {
    let cancelled = false;

    const fetchOrderHistory = async () => {
      if (!customerNumber) {
        setState((prev) => ({ ...prev, loading: false, error: true }));
        return;
      }

      setState((prev) => ({ ...prev, loading: true, error: false }));

      try {
        const pagedSalesOrderResponse = await delivererClient.getOrdersByCustomer(
          customerNumber,
          pageNumber,
          pageSize
        );

        const deliveryDates = (pagedSalesOrderResponse.items ?? []).flatMap((item) =>
          item.deliveries.map((delivery) => delivery.requestedDeliveryMoment)
        );

        const weeklyMenuKeysToFetch = Array.from(
          new Set(
            deliveryDates
              .map((date) => getWeeklyMenuKeyForDate(date, activeWeeklyMenus))
              .filter((key): key is string => !!key)
          )
        );

        const deliveries = await Promise.all(
          (pagedSalesOrderResponse.items ?? []).flatMap((item) =>
            item.deliveries.map(async (delivery) => {
              return {
                salesOrderId: item.salesOrderId,
                deliveryId: delivery.deliveryId,
                status: delivery.status,
                processStatus: delivery.processStatus,
                rejectionStatus: delivery.rejectionStatus,
                deliveryStatus: delivery.deliveryStatus,
                requestedDeliveryMoment: delivery.requestedDeliveryMoment,
                alternateDeliveryAddress: delivery.alternateDeliveryAddress,
                items: await mapSalesOrderItemsWithProductInfo(delivery.salesOrderItems),
                actions: delivery.actions
              };
            })
          )
        );

        const weeklyMenusByKey = new Map(
          await Promise.all(
            weeklyMenuKeysToFetch.map(
              async (key) => [key, await delivererClient.getWeeklyMenu(key)] as const
            )
          )
        );

        // Append sequence numbers from the weekly menu to the delivery items
        deliveries.forEach((delivery) => {
          const weeklyMenuKey = getWeeklyMenuKeyForDate(
            delivery.requestedDeliveryMoment,
            activeWeeklyMenus
          );
          const matchingWeeklyMenu = weeklyMenuKey
            ? weeklyMenusByKey.get(weeklyMenuKey)
            : undefined;

          if (matchingWeeklyMenu) {
            delivery.items.forEach((item) => {
              const matchingMenuItem = matchingWeeklyMenu.items.find((menuItem) =>
                menuItem.portions?.some((portion) => portion.productId === item.productId)
              );

              if (matchingMenuItem) {
                item.sequenceNumberOnWeeklyMenu = matchingMenuItem.mealSequenceNumber;
                return;
              }

              const matchingMenuBox = matchingWeeklyMenu.boxes.find((menuItem) =>
                menuItem.portions?.some((portion) => portion.productId === item.productId)
              );

              if (matchingMenuBox) {
                item.sequenceNumberOnWeeklyMenu = matchingMenuBox.mealSequenceNumber; 
              }
            });
          }
        });

        if (!cancelled) {
          setState({
            deliveries,
            loading: false,
            error: false,
            metadata: pagedSalesOrderResponse.metadata
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ deliveries: [], loading: false, error: true, metadata: undefined });
        }
      }
    };

    fetchOrderHistory();

    return () => {
      cancelled = true;
    };
  }, [customerNumber, pageNumber, pageSize, activeWeeklyMenus]);

  const nextPage = () => {
    if (state.metadata?.hasNextPage) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const prevPage = () => {
    if (state.metadata?.hasPreviousPage && pageNumber > 1) {
      setPageNumber((prev) => prev - 1);
    }
  };

  const setPage = (page: number) => {
    setPageNumber(Math.max(1, page));
  };

  const setSize = (size: number) => {
    setPageSize(Math.max(1, size));
  };

  return {
    ...state,
    pageNumber,
    pageSize,
    nextPage,
    prevPage,
    setPage,
    setPageSize: setSize
  };
};
